import type { ParseKeys } from "i18next";

import type { IFastButtonVariantsData } from "@components/fastFields/fastButton/types";
import {
  IButtonModeEnum,
  IButtonSizeEnum,
  IButtonStyleEnum,
  IButtonVariantsEnum,
} from "@components/molecules/button";

export const fastButtonVariants = (
  entityName?: ParseKeys<"entity">,
): IFastButtonVariantsData => ({
  saveAndSend: {
    label: (t) => t("register"),
    size: IButtonSizeEnum.Small,
    mode: IButtonModeEnum.Submit,
  },
  cancelAndBack: {
    label: (t) => t("cancelAndBack"),
    styleType: IButtonStyleEnum.Fancy,
    variant: IButtonVariantsEnum.Basic,
    size: IButtonSizeEnum.Small,
  },
  close: {
    label: (t) => t("close"),
    styleType: IButtonStyleEnum.Fancy,
    variant: IButtonVariantsEnum.Basic,
    size: IButtonSizeEnum.Small,
  },
  save: {
    label: (t) => t("saveChanges"),
    size: IButtonSizeEnum.Small,
    mode: IButtonModeEnum.Submit,
  },
  back: {
    label: (t) => t("back"),
    startIcon: "ArrowRightLine",
    styleType: IButtonStyleEnum.Stroke,
    variant: IButtonVariantsEnum.Neutral,
    size: IButtonSizeEnum.Small,
  },
  cancel: {
    label: (t) => t("cancel"),
    styleType: IButtonStyleEnum.Stroke,
    variant: IButtonVariantsEnum.Neutral,
    size: IButtonSizeEnum.Small,
  },
  logout: {
    label: (t) => t("logout"),
    styleType: IButtonStyleEnum.Stroke,
    variant: IButtonVariantsEnum.Error,
    size: IButtonSizeEnum.Small,
    startIcon: "LogoutBoxLine",
  },
  cancelOperation: {
    label: (t) => t("cancelOperation"),
    styleType: IButtonStyleEnum.Stroke,
    variant: IButtonVariantsEnum.Neutral,
    size: IButtonSizeEnum.Small,
    startIcon: "CloseLine",
  },
  saveChanges: {
    label: (t) => t("saveChanges"),
    size: IButtonSizeEnum.Small,
    mode: IButtonModeEnum.Submit,
    startIcon: "CheckLine",
  },

  deleteOf: {
    label: (t) =>
      t("deleteOf", { name: entityName ? t(`entity:${entityName}`) : "" }),
    styleType: IButtonStyleEnum.Filled,
    variant: IButtonVariantsEnum.Error,
    size: IButtonSizeEnum.X_Small,
  },
  copyID: {
    label: (t) => t("copyID"),
    variant: IButtonVariantsEnum.Basic,
    size: IButtonSizeEnum.X_Small,
  },
  addNewOf: {
    label: (t) =>
      t("addNew", { name: entityName ? t(`entity:${entityName}`) : "" }),
    size: IButtonSizeEnum.Small,
    startIcon: "AddLine",
  },
  createNewOf: {
    label: (t) =>
      t("addNewOf", { name: entityName ? t(`entity:${entityName}`) : "" }),
    size: IButtonSizeEnum.Small,
    startIcon: "AddLine",
  },
  defineOf: {
    label: (t) =>
      t(entityName ? "defineOf" : "defineOfNew", {
        name: entityName ? t(`entity:${entityName}`) : "",
      }),
    size: IButtonSizeEnum.Small,
    startIcon: entityName ? "UserFollowLine" : "AddLine",
  },
  resetPassword: {
    label: (t) => t("resetPassword"),
    size: IButtonSizeEnum.X_Small,
    variant: IButtonVariantsEnum.Neutral,
    styleType: IButtonStyleEnum.Fancy,
    endIcon: "ArrowLeftSLine",
  },
  trainingCourseAssistantManagement: {
    label: (t) => t("trainingCourseAssistantManagement"),
    size: IButtonSizeEnum.X_Small,
    variant: IButtonVariantsEnum.Neutral,
    styleType: IButtonStyleEnum.Fancy,
    endIcon: "ArrowLeftSLine",
  },
  activate: {
    label: (t) => t("activate"),
    size: IButtonSizeEnum.Small,
    variant: IButtonVariantsEnum.Success,
  },
  deactivate: {
    label: (t) => t("deActivate"),
    size: IButtonSizeEnum.Small,
    variant: IButtonVariantsEnum.Error,
  },
  sendMessage: {
    label: (t) => t("sendMessage"),
    startIcon: "Message3Line",
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Button,
    styleType: IButtonStyleEnum.Fancy,
  },
  enterTheCode: {
    label: (t) => t("enterTheCode"),
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Button,
    styleType: IButtonStyleEnum.Fancy,
  },
  submitPurchaseRequest: {
    label: (t) => t("submitPurchaseRequest"),
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Button,
    styleType: IButtonStyleEnum.Fancy,
  },

  buyCourse: {
    label: (t) => t("buyCourse"),
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Button,
    styleType: IButtonStyleEnum.Fancy,
  },
  alreadyBuyTheCourse: {
    label: (t) => t("alreadyBuyTheCourse"),
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Button,
    styleType: IButtonStyleEnum.Fancy,
  },
  alreadySignupTheCourse: {
    label: (t) => t("alreadySignupTheCourse"),
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Button,
    styleType: IButtonStyleEnum.Fancy,
  },
  buy: {
    label: (t) => t("buy"),
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Button,
    styleType: IButtonStyleEnum.Fancy,
  },
  signUpInCourse: {
    label: (t) => t("signUpInCourse"),
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Button,
    styleType: IButtonStyleEnum.Fancy,
  },

  writeComment: {
    label: (t) => t("writeComment"),
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Button,
    styleType: IButtonStyleEnum.Fancy,
  },
  startExam: {
    label: (t) => t("startExam"),
    variant: IButtonVariantsEnum.Primary,
    startIcon: "RocketLine",
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Button,
    styleType: IButtonStyleEnum.Fancy,
  },
  reEnterTheExam: {
    label: (t) => t("reEnterTheExam"),
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Button,
    styleType: IButtonStyleEnum.Fancy,
  },
  checkTheAnswers: {
    label: (t) => t("checkTheAnswers"),
    variant: IButtonVariantsEnum.Primary,
    startIcon: "QuestionnaireLine",
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Button,
    styleType: IButtonStyleEnum.Fancy,
  },
  participated: {
    label: (t) => t("participated"),
    variant: IButtonVariantsEnum.Primary,
    startIcon: "CheckboxCircleLine",
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Button,
    styleType: IButtonStyleEnum.Fancy,
  },
  reTakeTheExam: {
    label: (t) => t("reTakeTheExam"),
    variant: IButtonVariantsEnum.Primary,
    startIcon: "RestartLine",
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Button,
    styleType: IButtonStyleEnum.Fancy,
  },
  editAnswers: {
    label: (t) => t("editAnswers"),
    variant: IButtonVariantsEnum.Basic,
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Button,
    styleType: IButtonStyleEnum.Fancy,
  },
  finalSubmit: {
    label: (t) => t("finalSubmit"),
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Submit,
    styleType: IButtonStyleEnum.Fancy,
  },

  checkCorrectAnswered: {
    label: (t) => t("checkCorrectAnswered"),
    variant: IButtonVariantsEnum.Basic,
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Button,
    styleType: IButtonStyleEnum.Fancy,
  },

  viewDescription: {
    label: (t) => t("viewDescription"),
    variant: IButtonVariantsEnum.Basic,
    startIcon: "LightbulbLine",
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Button,
    styleType: IButtonStyleEnum.Fancy,
  },
  backFancy: {
    label: (t) => t("backFancy"),
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Button,
    styleType: IButtonStyleEnum.Fancy,
  },
  done: {
    label: (t) => t("done"),
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Button,
    styleType: IButtonStyleEnum.Fancy,
  },

  nextQuestion: {
    label: (t) => t("nextQuestion"),
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Button,
    styleType: IButtonStyleEnum.Fancy,
  },
  login: {
    label: (t) => t("page:auth.login.actions.login"),
    styleType: IButtonStyleEnum.Filled,
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
  },
  submitAndSendCode: {
    label: (t) => t("page:auth.codeVerification.actions.submitCode"),
    styleType: IButtonStyleEnum.Filled,
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
  },

  resetPasswordLogin: {
    label: (t) => t("page:auth.resetPassword.actions.resetPassword"),
    styleType: IButtonStyleEnum.Filled,
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
  },
  returnToMainPage: {
    label: (t) => t("returnToMainPage"),
    variant: IButtonVariantsEnum.Basic,
    size: IButtonSizeEnum.Small,
    styleType: IButtonStyleEnum.Fancy,
  },
  sendNewNotif: {
    label: (t) => t("sendNewNotif"),
    size: IButtonSizeEnum.Small,
    startIcon: "AddLine",
  },
  viewAll: {
    label: (t) => t("viewAll"),
    size: IButtonSizeEnum.X_Small,
    variant: IButtonVariantsEnum.Basic,
  },
  publishExam: {
    label: (t) => t("publishExam"),
    size: IButtonSizeEnum.Small,
    styleType: IButtonStyleEnum.Fancy,
    startIcon: "Share2Line",
  },
  addNewExam: {
    label: (t) => t("addNew", { name: t(`entity:exam`) }),
    size: IButtonSizeEnum.X_Small,
    startIcon: "AddLine",
    styleType: IButtonStyleEnum.Fancy,
    variant: IButtonVariantsEnum.Basic,
  },
  approveOf: {
    label: (t) =>
      t("approveOf", { name: entityName ? t(`entity:${entityName}`) : "" }),
    styleType: IButtonStyleEnum.Filled,
    variant: IButtonVariantsEnum.Success,
    size: IButtonSizeEnum.X_Small,
  },
  disapproveOf: {
    label: (t) =>
      t("disapproveOf", { name: entityName ? t(`entity:${entityName}`) : "" }),
    styleType: IButtonStyleEnum.Filled,
    variant: IButtonVariantsEnum.Error,
    size: IButtonSizeEnum.X_Small,
  },
  sendMessageTo: {
    label: (t) =>
      t("sendMessageTo", { name: entityName ? t(`entity:${entityName}`) : "" }),
    startIcon: "SendPlane2Line",
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Button,
    styleType: IButtonStyleEnum.Fancy,
  },
  sendMessageToIcon: {
    startIcon: "SendPlane2Line",
    variant: IButtonVariantsEnum.Neutral,
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Button,
    styleType: IButtonStyleEnum.Stroke,
  },
  loadMore: {
    label: (t) => t("loadMore"),
    size: IButtonSizeEnum.X_Small,
    startIcon: "AddLine",
    styleType: IButtonStyleEnum.Fancy,
    variant: IButtonVariantsEnum.Basic,
  },
  sendFile: {
    label: (t) => t("sendFile"),
    size: IButtonSizeEnum.Small,
    styleType: IButtonStyleEnum.Fancy,
    startIcon: "SendPlane2Line",
  },
  finishExam: {
    label: (t) => t("finishExam"),
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Medium,
    mode: IButtonModeEnum.Submit,
    styleType: IButtonStyleEnum.Fancy,
  },
  saveChangesWithoutIcon: {
    label: (t) => t("saveChanges"),
    size: IButtonSizeEnum.Small,
    mode: IButtonModeEnum.Submit,
  },
  cancelOperationWithoutIcon: {
    label: (t) => t("cancelOperation"),
    styleType: IButtonStyleEnum.Stroke,
    variant: IButtonVariantsEnum.Neutral,
    size: IButtonSizeEnum.Small,
  },
  next: {
    label: (t) => t("page:auth.signUpPage.actions.next"),
    size: IButtonSizeEnum.Medium,
    variant: IButtonVariantsEnum.Primary,
    styleType: IButtonStyleEnum.Fancy,
  },
  signUp: {
    label: (t) => t("page:auth.signUp"),
    size: IButtonSizeEnum.Medium,
    variant: IButtonVariantsEnum.Primary,
    styleType: IButtonStyleEnum.Fancy,
  },
  tryAgain: {
    label: (t) => t("retry"),
    variant: IButtonVariantsEnum.Basic,
    size: IButtonSizeEnum.Small,
    styleType: IButtonStyleEnum.Fancy,
  },
  returnToMainPagePrimary: {
    label: (t) => t("returnToMainPage"),
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Small,
    styleType: IButtonStyleEnum.Fancy,
  },
  contact: {
    label: (t) => t("contact"),
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Small,
    styleType: IButtonStyleEnum.Fancy,
  },
  returnToApplication: {
    label: (t) => t("returnToApplication"),
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Small,
    styleType: IButtonStyleEnum.Fancy,
  },
  deleteAccount: {
    label: (t) => t("removeAccount"),
    variant: IButtonVariantsEnum.Error,
    size: IButtonSizeEnum.Medium,
    styleType: IButtonStyleEnum.Stroke,
    startIcon: "UserForbidLine",
  },
  yesDeleteAccount: {
    label: (t) => t("yesDeleteAccount"),
    variant: IButtonVariantsEnum.Error,
    size: IButtonSizeEnum.Small,
    styleType: IButtonStyleEnum.Fancy,
  },
  confirmAndDeleteAccount: {
    label: (t) => t("confirmDeleteAccount"),
    variant: IButtonVariantsEnum.Error,
    size: IButtonSizeEnum.Small,
    styleType: IButtonStyleEnum.Fancy,
  },
  submitAndSendAnswer: {
    label: (t) => t("submitAndSendAnswer"),
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Small,
    styleType: IButtonStyleEnum.Fancy,
  },
  download: {
    label: (t) => t("download"),
    styleType: IButtonStyleEnum.Fancy,
    variant: IButtonVariantsEnum.Primary,
    size: IButtonSizeEnum.Small,
  },
  printCodes: {
    label: (t) => t("component:codeLists.printCodesButton"),
    size: IButtonSizeEnum.X_Small,
    variant: IButtonVariantsEnum.Basic,
    styleType: IButtonStyleEnum.Fancy,
    startIcon: "PrinterLine",
  },
});
